import { Component, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { AutoComplete } from 'primeng/autocomplete';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { MenuModule } from 'primeng/menu';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { filter, first, takeUntil } from 'rxjs/operators';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { NotificationApiService } from '../../../services/notification-api.service';
import { SignalRService } from '../../../services/signalr.service';
import { SSOService, SSOUser } from '../../../services/sso.service';
import { ProjectStateService } from '../../../services/project-state.service';
import { Profile } from '../../../features/profile/profile';
import { WorkLocationPrompt } from '../../../features/work-location-prompt/work-location-prompt';
import { UserService } from '../../../services/user.service';
import { NotificationPanel } from '../notification-panel/notification-panel';

@Component({
  selector: 'app-top-menu',
  imports: [CommonModule, ButtonModule, AvatarModule, MenuModule, InputTextModule, SelectModule, AutoCompleteModule, FormsModule, NotificationPanel, RouterLink],
  templateUrl: './top-menu.html',
  styleUrl: './top-menu.css'
})
export class TopMenuComponent implements OnInit, OnDestroy {
  private static readonly SUPER_ADMIN_ROLE = 'super admin';
  private destroy$ = new Subject<void>();
  ddRef: DynamicDialogRef | null = null;
  private workLocationRef: DynamicDialogRef | null = null;
  private workLocationChecked = false;
  userName = '';
  userEmail = '';
  userInitials = '';
  searchText = '';
  searchPlaceholder = 'Search by project...';
  showSearch = true;
  showMapDropdown = false;
  showMapFilterSelector = false;
  mapFilterMode: 'project' | 'user' = 'project';
  mapFilterOptions = [
    { label: 'Project', value: 'project' },
    { label: 'User', value: 'user' }
  ];
  allMapSuggestions: string[] = [];
  mapSuggestions: string[] = [];
  @ViewChild('mapAutoComplete') mapAutoComplete!: AutoComplete;
  private projectSearchRoutes = ['/projects'];
  private projectPageRoutes: { prefix: string; placeholder: string }[] = [
    { prefix: '/equipment/', placeholder: 'Search MV Power Stations...' },
    { prefix: '/tasks/', placeholder: 'Search tasks...' },
    { prefix: '/plans/', placeholder: 'Search plans...' },
    { prefix: '/photos/', placeholder: 'Search photos...' },
    { prefix: '/forms/', placeholder: 'Search forms...' },
    { prefix: '/form-detail/', placeholder: 'Search templates...' },
    { prefix: '/files/', placeholder: 'Search files...' },
    { prefix: '/blockschedule/', placeholder: 'Search schedules...' },
    { prefix: '/projectusers/', placeholder: 'Search users...' },
    { prefix: '/projecttemplates/', placeholder: 'Search templates...' },
    { prefix: '/projectsettings/', placeholder: '' },
    { prefix: '/dashboard/', placeholder: '' },
  ];
  unreadCount = signal(0);
  showNotificationPanel = signal(false);

  userMenuItems = [
    { label: 'Profile', icon: 'ri-user-line', command: () => this.openProfile() },
    { separator: true },
    { label: 'Sign out', icon: 'ri-logout-box-r-line', command: () => this.onLogout() }
  ];

  constructor(
    private ssoService: SSOService,
    private router: Router,
    private dialogService: DialogService,
    private projectStateService: ProjectStateService,
    private notificationApiService: NotificationApiService,
    private signalRService: SignalRService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.ssoService.user$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((user: SSOUser | null) => {
      this.updateUserInfo(user);
      if (user?.userId && user?.userTypeName && !this.workLocationChecked) {
        this.workLocationChecked = true;
        this.checkWorkLocationPrompt(user);
      }
    });

    const currentUser = this.ssoService.getCurrentUser();
    if (currentUser) {
      this.updateUserInfo(currentUser);
    }

    this.updateSearchContext(this.router.url);

    this.router.events.pipe(
      takeUntil(this.destroy$),
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe((event) => {
      this.updateSearchContext(event.urlAfterRedirects);
    });

    this.notificationApiService.unreadCount$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(count => {
      this.unreadCount.set(count);
    });

    this.notificationApiService.refreshUnreadCount();

    this.signalRService.notification$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(notification => {
      if (notification) {
        this.notificationApiService.incrementUnreadCount();
      }
    });

    this.projectStateService.mapAllSuggestions$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(suggestions => {
      this.allMapSuggestions = suggestions;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateUserInfo(user: SSOUser | null): void {
    this.configureMapFilterForRole(user);

    if (user) {
      this.userName = user.name || 'Unknown User';
      this.userEmail = user.email || '';
      this.userInitials = this.generateInitials(user.name || '');
    } else {
      this.userName = '';
      this.userEmail = '';
      this.userInitials = '';
    }
  }

  private configureMapFilterForRole(user: SSOUser | null): void {
    const isSuperAdmin = (user?.roleName || '').trim().toLowerCase() === TopMenuComponent.SUPER_ADMIN_ROLE;

    this.showMapFilterSelector = isSuperAdmin;
    this.mapFilterOptions = isSuperAdmin
      ? [
          { label: 'Project', value: 'project' as const },
          { label: 'User', value: 'user' as const }
        ]
      : [
          { label: 'Project', value: 'project' as const }
        ];

    if (!isSuperAdmin && this.mapFilterMode !== 'project') {
      this.mapFilterMode = 'project';
      this.projectStateService.setMapFilterMode('project');
    }
  }

  private generateInitials(name: string): string {
    if (!name) return '';
    const nameParts = name.trim().split(' ');
    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }
    return nameParts[0].charAt(0).toUpperCase() + nameParts[nameParts.length - 1].charAt(0).toUpperCase();
  }

  async onLogout(): Promise<void> {
    try {
      await this.ssoService.logout();
    } catch (error) {
      console.error('Logout failed:', error);
      localStorage.clear();
      this.router.navigate(['/login']);
    }
  }

  openProfile(): void {
    const currentUser = this.ssoService.getCurrentUser();
    this.ddRef = this.dialogService.open(Profile, {
      header: 'User Profile',
      closable: true,
      width: '45rem',
      modal: true,
      maximizable: true,
      styleClass: 'kansei-dialog',
      contentStyle: { overflow: 'hidden', height: '80vh' },
      data: {
        userId:     currentUser?.userId     ?? 0,
        userTypeId: currentUser?.userTypeId ?? 0,
        roleId:     currentUser?.roleId     ?? 0
      }
    });
  }

  private checkWorkLocationPrompt(user: SSOUser): void {
    const type = (user.userTypeName || '').trim().toUpperCase();
    if (type !== 'TMEIC' && type !== 'ASP') return;

    this.userService.getUserById(user.userId!).pipe(first()).subscribe(fullUser => {
      if (!fullUser.city || !fullUser.state) {
        this.workLocationRef = this.dialogService.open(WorkLocationPrompt, {
          header: 'Work Location Required',
          closable: true,
          width: '40rem',
          modal: true,
          styleClass: 'kansei-dialog',
          contentStyle: { overflow: 'hidden', height: '70vh' },
          data: {
            userId:   user.userId,
            address1: fullUser.address1,
            address2: fullUser.address2,
            city:     fullUser.city,
            state:    fullUser.state,
            zip:      fullUser.zip,
            country:  fullUser.country
          }
        });
      }
    });
  }

  toggleNotificationPanel(): void {
    this.showNotificationPanel.update(value => !value);
  }

  closeNotificationPanel(): void {
    this.showNotificationPanel.set(false);
  }

  onMapFilterModeChange(mode: 'project' | 'user'): void {
    this.mapFilterMode = mode;
    this.projectStateService.setMapFilterMode(mode);
    this.projectStateService.setPageSearch('');
    this.searchText = '';
    this.mapSuggestions = [];
  }

  onMapSearch(event: { query: string }): void {
    const query = (event.query || '').toLowerCase().trim();
    this.mapSuggestions = query === ''
      ? this.allMapSuggestions
      : this.allMapSuggestions.filter(s => s.toLowerCase().includes(query));
  }

  onMapInputFocus(): void {
    this.onMapSearch({ query: this.searchText });
    this.mapAutoComplete?.show();
  }

  clearSearch(): void {
    this.searchText = '';
    this.onSearchChange('');
  }

  onSearchChange(value: string): void {
    if (this.shouldUseProjectSearch(this.router.url)) {
      this.projectStateService.setProjectListSearch(value ?? '');
    } else {
      this.projectStateService.setPageSearch(value ?? '');
    }
  }

  private shouldUseProjectSearch(url: string): boolean {
    return this.projectSearchRoutes.some(route => url.startsWith(route))
      && !url.startsWith('/projectsettings');
  }

  private updateSearchContext(url: string): void {
    // Clear page search on every navigation
    this.projectStateService.setPageSearch('');
    this.searchText = '';
    this.showMapDropdown = false;
    this.mapSuggestions = [];

    // Home / dashboard — hide search
    if (url === '/home' || url.startsWith('/home/') || url === '/') {
      this.showSearch = false;
      return;
    }

    // Maps — show dropdown + autocomplete search
    if (url.startsWith('/maps')) {
      this.showSearch = true;
      this.showMapDropdown = true;
      this.searchPlaceholder = 'Search...';
      this.mapFilterMode = this.showMapFilterSelector
        ? this.projectStateService.getMapFilterMode()
        : 'project';
      return;
    }

    // Project list
    if (this.shouldUseProjectSearch(url)) {
      this.showSearch = true;
      this.searchPlaceholder = 'Search by project...';
      this.searchText = this.projectStateService.getProjectListSearch();
      return;
    }

    // User management
    if (url.startsWith('/users')) {
      this.showSearch = true;
      this.searchPlaceholder = 'Search users...';
      return;
    }

    // Templates
    if (url.startsWith('/templates')) {
      this.showSearch = true;
      this.searchPlaceholder = 'Search templates...';
      return;
    }

    // Task management
    if (url.startsWith('/task-management')) {
      this.showSearch = true;
      this.searchPlaceholder = 'Search...';
      return;
    }

    // Role management
    if (url.startsWith('/role-management')) {
      this.showSearch = true;
      this.searchPlaceholder = 'Search pages...';
      return;
    }

    // Project-specific pages — context-aware search
    const projectPage = this.projectPageRoutes.find(r => url.startsWith(r.prefix));
    if (projectPage) {
      if (projectPage.placeholder) {
        this.showSearch = true;
        this.searchPlaceholder = projectPage.placeholder;
      } else {
        this.showSearch = false;
      }
      return;
    }

    // All other routes — hide search
    this.showSearch = false;
  }
}
